import { useState } from 'react';
import { Key, UserPlus, RotateCcw, Trash2, AlertCircle, CheckCircle } from 'lucide-react';
import { API_BASE, fetchApi, getAuthHeaders } from '../../services/apiClient';

type Tab = 'create-user' | 'reset-password';

export default function SecurityPage() {
  const [activeTab, setActiveTab] = useState<Tab>('create-user');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Create User form state
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });

  // Reset Password form state
  const [resetForm, setResetForm] = useState({
    email: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Delete User form state
  const [deleteForm, setDeleteForm] = useState({
    email: '',
    confirmDelete: '',
  });

  const resetAllMessages = () => {
    setError('');
    setSuccessMessage('');
  };

  // ━━━ CREATE USER ━━━
  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    resetAllMessages();

    if (!createForm.email.trim()) {
      setError('Email is required');
      return;
    }

    if (!createForm.email.includes('@') || !createForm.email.includes('.')) {
      setError('Invalid email format');
      return;
    }

    if (createForm.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (createForm.password !== createForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetchApi(`${API_BASE}/admin/create-user`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          email: createForm.email,
          password: createForm.password,
        }),
      });

      const data = await response.json();

      if (response.status === 400 && data.message?.includes('already exists')) {
        setError('User already exists');
        return;
      }

      if (!response.ok) {
        setError(data.message || 'Failed to create user');
        return;
      }

      setSuccessMessage(`User ${createForm.email} created successfully`);
      setCreateForm({ email: '', password: '', confirmPassword: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  // ━━━ RESET PASSWORD ━━━
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    resetAllMessages();

    if (!resetForm.email.trim()) {
      setError('Email is required');
      return;
    }

    if (resetForm.newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (resetForm.newPassword !== resetForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetchApi(`${API_BASE}/admin/reset-password`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          email: resetForm.email,
          new_password: resetForm.newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed to reset password');
        return;
      }

      setSuccessMessage(`Password reset successfully for ${resetForm.email}`);
      setResetForm({ email: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  // ━━━ DELETE USER ━━━
  const handleDeleteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    resetAllMessages();

    if (!deleteForm.email.trim()) {
      setError('Email is required');
      return;
    }

    if (deleteForm.confirmDelete !== 'DELETE') {
      setError('Type "DELETE" to confirm user removal');
      return;
    }

    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetchApi(`${API_BASE}/admin/delete-user`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ email: deleteForm.email }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed to delete user');
        return;
      }

      setSuccessMessage(`User ${deleteForm.email} has been marked as inactive`);
      setDeleteForm({ email: '', confirmDelete: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <Key size={24} className="text-blue-600" />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Security</h1>
            <p className="text-sm text-gray-500">User management and password administration</p>
          </div>
        </div>

        {/* Tab navigation */}
        <div className="flex gap-1 mt-4">
          <button
            onClick={() => {
              setActiveTab('create-user');
              resetAllMessages();
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'create-user'
                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                : 'text-gray-600 hover:bg-gray-100 border border-transparent'
            }`}
          >
            <UserPlus size={16} />
            New User Creation
          </button>
          <button
            onClick={() => {
              setActiveTab('reset-password');
              resetAllMessages();
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'reset-password'
                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                : 'text-gray-600 hover:bg-gray-100 border border-transparent'
            }`}
          >
            <RotateCcw size={16} />
            Reset Password
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Messages */}
        {error && (
          <div className="mb-4 flex gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle size={20} className="text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        {successMessage && (
          <div className="mb-4 flex gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-700">{successMessage}</p>
          </div>
        )}

        {/* Create User Tab */}
        {activeTab === 'create-user' && (
          <div className="max-w-xl">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Create New User</h2>
              <p className="text-sm text-gray-600 mb-6">
                Create a new user account and set their initial password.
              </p>

              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={createForm.email}
                    onChange={e => setCreateForm({ ...createForm, email: e.target.value })}
                    placeholder="user@example.com"
                    autoComplete="off"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    value={createForm.password}
                    onChange={e => setCreateForm({ ...createForm, password: e.target.value })}
                    placeholder="Minimum 8 characters"
                    autoComplete="new-password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={createForm.confirmPassword}
                    onChange={e => setCreateForm({ ...createForm, confirmPassword: e.target.value })}
                    placeholder="Repeat password"
                    autoComplete="new-password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create User'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Reset Password Tab */}
        {activeTab === 'reset-password' && (
          <div className="max-w-2xl space-y-6">
            {/* Reset Password Section */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Reset User Password</h2>
              <p className="text-sm text-gray-600 mb-6">
                Set a new password for an existing user account.
              </p>

              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    User Email
                  </label>
                  <input
                    type="email"
                    value={resetForm.email}
                    onChange={e => setResetForm({ ...resetForm, email: e.target.value })}
                    placeholder="user@example.com"
                    autoComplete="off"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={resetForm.newPassword}
                    onChange={e => setResetForm({ ...resetForm, newPassword: e.target.value })}
                    placeholder="Minimum 8 characters"
                    autoComplete="new-password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={resetForm.confirmPassword}
                    onChange={e => setResetForm({ ...resetForm, confirmPassword: e.target.value })}
                    placeholder="Repeat password"
                    autoComplete="new-password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            </div>

            {/* Delete User Section */}
            <div className="bg-white border border-red-200 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Trash2 size={20} className="text-red-600" />
                <h2 className="text-lg font-semibold text-gray-900">Delete User</h2>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Remove a user from the system. The user will be marked as inactive and will not be able to log in. User data and chat history will be preserved for audit purposes.
              </p>

              <form onSubmit={handleDeleteUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    User Email to Delete
                  </label>
                  <input
                    type="email"
                    value={deleteForm.email}
                    onChange={e => setDeleteForm({ ...deleteForm, email: e.target.value })}
                    placeholder="user@example.com"
                    autoComplete="off"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Deletion (type "DELETE")
                  </label>
                  <input
                    type="text"
                    value={deleteForm.confirmDelete}
                    onChange={e => setDeleteForm({ ...deleteForm, confirmDelete: e.target.value })}
                    placeholder='Type "DELETE" to confirm'
                    className="w-full px-3 py-2 border border-red-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 font-mono"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This is a destructive action. Type DELETE to proceed.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading || deleteForm.confirmDelete !== 'DELETE'}
                  className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Deleting...' : 'Delete User'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
